import { useQueries } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'

import { FinanceInvoice, FinanceService } from '@/api/models/finance'
import { useFinanceInvoiceList } from '@/api/models/finance/hooks/useFinanceInvoiceList'
import { ApiPagedPayload } from '@/api/shared/types'
import { useSelectedProjectStore } from '@/components/entities/common/model/selected-project.store'
import { firstPage } from '@/components/pages/bill/model/constants'

export const INVOICE_LIST_DRAWER_QUERY_KEY = 'invoice-list-drawer'

type Props = {
  isPaid?: boolean
  merchantId?: string
}

export const useInvoiceListDrawer = ({
  isPaid = false,
  merchantId,
  page
}: ApiPagedPayload<Props>) => {
  const { selectedProjectId: projectId } = useSelectedProjectStore()

  const { invoices, totalPages, isError, ...rest } = useFinanceInvoiceList({
    isPaid,
    merchantId,
    page
  })

  const [firstPageInvoices, setFirstPageInvoices] = useState<FinanceInvoice[]>(
    []
  )
  const [restPages, setRestPages] = useState<number[]>([])

  useEffect(() => {
    if (page !== firstPage) {
      return
    }
    setFirstPageInvoices(invoices)
    setRestPages(
      Array.from(
        { length: (totalPages ?? 0) - firstPage },
        (_, index) => index + 1 + firstPage
      )
    )
  }, [invoices, page, totalPages])

  const { data: restInvoices, isError: isRestError } = useQueries({
    queries: restPages.map(restPage => ({
      queryKey: [
        INVOICE_LIST_DRAWER_QUERY_KEY,
        projectId,
        isPaid,
        merchantId,
        restPage
      ],
      queryFn: () =>
        FinanceService.getInvoices({
          projectId,
          isPaid,
          merchantId,
          page: restPage
        })
    })),
    combine: results => ({
      data: results.map(result => result.data?.data.data),
      isError: results.some(result => result.isError)
    })
  })

  const invoiceIds = useMemo(() => {
    const firstPageInvoiceIds = firstPageInvoices.map(({ id }) => id)
    const restInvoiceIds = restInvoices.reduce(
      (acc: string[], pagedInvoices) => [
        ...acc,
        ...(pagedInvoices?.map(({ id }) => id) || [])
      ],
      []
    )

    return [...firstPageInvoiceIds, ...restInvoiceIds]
  }, [firstPageInvoices, restInvoices])

  return {
    ...rest,
    invoices,
    invoiceIds,
    totalPages,
    isError: isError || isRestError
  }
}
